import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import FileInputComponent from "react-file-input-previews-base64";
import { FormattedMessage } from "react-intl";
import { Field } from "formik";
import FormFieldError from "components/form/form-field-error";
import { formikPropTypes } from "components/common";
import FormHelperText from "components/form/form-helper-text";
import { injectIntl } from "react-intl";

const ValidatedImageField = ({
  field: { name, value, onChange, onBlur },
  id,
  label,
  imagePreview = true,
  accept = "image/*",
  multiple,
  showOldPreview = false,
  intl,
}) => {
  const [show, setShow] = useState(false);
  const [isFileSelected, setIsFileSelected] = useState(!!value);
  const recoveredValue = useRef(value);

  return (
    <>
      <FileInputComponent
        inputId={id}
        inputName={name}
        labelText={label}
        imagePreview={imagePreview}
        value={value}
        accept={accept}
        multiple={multiple}
        callbackFunction={data => {
          setIsFileSelected(!!data);
          if (data && imagePreview) {
            setShow(true);

            var image = new Image();
            image.onload = () => {
              const size = { x: image.width, y: image.height };
              return !data || data.length <= 0 ? onChange({}) : onChange({ ...data[0], size });
            };
            image.src = data[0].base64;
          }
        }}
        labelStyle={{ fontSize: 14, marginRight: 20 }}
        buttonComponent={
          isFileSelected ? (
            <button type="button">
              <FormattedMessage id="common.asset.modify" />
            </button>
          ) : (
            <button type="button">
              <FormattedMessage id="common.asset.upload" />
            </button>
          )
        }
      />
      {!showOldPreview && !show && <FormHelperText text={intl.formatMessage({ id: "common.asset.notFound" })} />}
      {showOldPreview && recoveredValue.current && recoveredValue.current === value && (
        <img style={{ height: "30vmin" }} src={value} alt={name} />
      )}
      <FormFieldError name={name} />
    </>
  );
};

ValidatedImageField.propTypes = {
  ...formikPropTypes,
  /**
   * Id del input
   */
  id: PropTypes.string,
  /**
   * Etiqueta del input
   */
  label: PropTypes.string,
  /**
   * Indica si acepta la subida de varias imágenes
   */
  multiple: PropTypes.bool,
  /**
   * Indica si se ve un thumbnail de la imagen seleccionada
   */
  imagePreview: PropTypes.bool,
  /**
   * Tipo MIME de imágenes que acepta
   */
  accept: PropTypes.string,
};

const I18NValidatedImageField = injectIntl(ValidatedImageField);

const FormImageField = ({ name, onChange, value, onBlur, ...props }) => {
  const handleChange = value => onChange(name)(value);
  return (
    <Field
      component={I18NValidatedImageField}
      field={{
        name,
        onChange: handleChange,
        value,
        onBlur,
      }}
      name={name}
      {...props}
    />
  );
};

FormImageField.propTypes = {
  /**
   * Indica si acepta la subida de varias imágenes
   */
  multiple: PropTypes.bool,
  /**
   * Indica si se ve un thumbnail de la imagen seleccionada
   */
  imagePreview: PropTypes.bool,
  /**
   * Tipo MIME de imágenes que acepta
   */
  accept: PropTypes.string,
  /**
   * Callback que se dispara con el cambio del input
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Etiqueta del input
   */
  label: PropTypes.string,
  /**
   * Nombre del campo
   */
  name: PropTypes.string,
  /**
   * Valor por defecto del campo
   */
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  /**
   * Callback que se dispara con el cambio de foco
   */
  onBlur: PropTypes.func,
};

export default FormImageField;
